import React, { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './SheetMapper.css'; // Reuse the same styles
import DeckGLMap from '../components/DeckGLMap';
import Sidebar from '../components/Sidebar';
import SheetSetupDialog from '../components/SheetSetupDialog';
import RecentSheetsTiles from '../components/RecentSheetsTiles';
import RecentSheetsSidebar from '../components/RecentSheetsSidebar';
import { fetchQuickbaseReport, getRecentReports, saveRecentReport, pinReport, unpinReport, updateReportName, createSharedMap } from '../services/api';
import { getColorAssignmentsObject, restoreColorAssignments, getHiddenGroupsArray, restoreHiddenGroups } from '../utils/markerUtils';
import qbMapIcon from '../icons/QBMapIcon.png';

function QuickbaseMapper() {
  const location = useLocation();
  const [reportUrl, setReportUrl] = useState('');
  const [markers, setMarkers] = useState([]);
  const [rawReportData, setRawReportData] = useState(null);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [reportConfig, setReportConfig] = useState(null);
  const [selectedMarkers, setSelectedMarkers] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [visibleMarkers, setVisibleMarkers] = useState([]);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('qbSidebarWidth');
    return saved ? parseInt(saved, 10) : 320;
  });
  const [colorRefreshKey, setColorRefreshKey] = useState(0);
  const [pinnedReports, setPinnedReports] = useState([]);
  const [recentReports, setRecentReports] = useState([]);

  // Load recent reports on mount
  useEffect(() => {
    loadRecentReports();
  }, []);

  // Auto-load shared map from query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('url') && params.get('config')) {
      const sharedUrl = params.get('url');
      const sharedMapName = params.get('mapName');

      setReportUrl(sharedUrl);
      setLoading(true);
      setError(null);

      try {
        const sharedConfig = JSON.parse(params.get('config'));
        setReportConfig(sharedConfig);

        // Restore color assignments and hidden groups if saved
        if (sharedConfig.colorAssignments) {
          restoreColorAssignments(sharedConfig.colorAssignments);
        }
        if (sharedConfig.hiddenGroups) {
          restoreHiddenGroups(sharedConfig.hiddenGroups);
        }

        const columnMapping = {
          latitude: sharedConfig.latColumn,
          longitude: sharedConfig.lngColumn,
        };

        fetchQuickbaseReport(sharedUrl, columnMapping)
          .then(data => {
            setMarkers(data.markers);
            setRawReportData(data);
            setVisibleMarkers(data.markers);

            // Save to recent reports with custom name if provided
            const colorAssignments = getColorAssignmentsObject();
            const hiddenGroups = getHiddenGroupsArray();
            saveRecentReport({
              url: sharedUrl,
              reportName: sharedMapName || data.reportName || `Report ${data.reportId}`,
              reportId: data.reportId,
              config: { ...sharedConfig, colorAssignments, hiddenGroups }
            }).then(updatedRecent => {
              setPinnedReports(updatedRecent.pinned || []);
              setRecentReports(updatedRecent.recent || []);
            }).catch(error => {
              console.error('Failed to update recent reports:', error);
            });

            setLoading(false);
          })
          .catch(err => {
            console.error('Failed to load report:', err);
            setError(err.message || 'Failed to load report');
            setLoading(false);
          });
      } catch (err) {
        console.error('Failed to parse shared map config:', err);
        setError('Failed to load shared map');
        setLoading(false);
      }
    }
  }, [location]);

  const loadRecentReports = async () => {
    try {
      const data = await getRecentReports();
      setPinnedReports(data.pinned || []);
      setRecentReports(data.recent || []);
    } catch (error) {
      console.error('Failed to load recent reports:', error);
    }
  };

  const handleLoadReport = async () => {
    if (!reportUrl.trim()) {
      setError('Please enter a QuickBase report URL');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchQuickbaseReport(reportUrl);
      setRawReportData(data);
      setShowSetupDialog(true);
    } catch (err) {
      console.error('Failed to load QuickBase report:', err);

      if (err.needsMapping && err.headers) {
        // Show setup dialog for manual column mapping
        setRawReportData({ headers: err.headers, markers: [], data: err.data });
        setShowSetupDialog(true);
      } else {
        setError(err.message || 'Failed to load QuickBase report');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetupComplete = useCallback(async (config) => {
    setReportConfig(config);
    setLoading(true);

    try {
      // Fetch report data again with the column mapping
      const columnMapping = {
        latitude: config.latColumn,
        longitude: config.lngColumn,
      };

      const data = await fetchQuickbaseReport(reportUrl, columnMapping);

      setMarkers(data.markers);
      setRawReportData(data);
      setVisibleMarkers(data.markers);
      setShowSetupDialog(false);

      // Save to recent reports with color assignments and hidden groups
      try {
        const colorAssignments = getColorAssignmentsObject();
        const hiddenGroups = getHiddenGroupsArray();
        const updatedRecent = await saveRecentReport({
          url: reportUrl,
          reportName: data.reportName || `Report ${data.reportId}`,
          reportId: data.reportId,
          config: { ...config, colorAssignments, hiddenGroups }
        });
        setPinnedReports(updatedRecent.pinned || []);
        setRecentReports(updatedRecent.recent || []);
      } catch (error) {
        console.error('Failed to save to recent reports:', error);
      }
    } catch (err) {
      console.error('Failed to load report with mapping:', err);
      setError(err.message || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [reportUrl]);

  const handleSetupCancel = useCallback(() => {
    setShowSetupDialog(false);
  }, []);

  const handleSelectionChange = useCallback((newSelection) => {
    setSelectedMarkers(newSelection);
  }, []);

  const handleFilterChange = useCallback((filteredMarkers) => {
    setVisibleMarkers(filteredMarkers);
  }, []);

  const handleGroupVisibilityChange = useCallback(() => {
    // Auto-save group visibility changes if a report is loaded
    if (reportUrl && reportConfig && rawReportData) {
      const colorAssignments = getColorAssignmentsObject();
      const hiddenGroups = getHiddenGroupsArray();
      saveRecentReport({
        url: reportUrl,
        reportName: rawReportData.reportName || `Report ${rawReportData.reportId}`,
        reportId: rawReportData.reportId,
        config: { ...reportConfig, colorAssignments, hiddenGroups }
      }).then(updatedRecent => {
        setPinnedReports(updatedRecent.pinned || []);
        setRecentReports(updatedRecent.recent || []);
      }).catch(error => {
        console.error('Failed to auto-save group visibility:', error);
      });
    }
  }, [reportUrl, reportConfig, rawReportData]);

  const handleColorChange = useCallback(() => {
    setColorRefreshKey(prev => prev + 1);

    // Auto-save color changes if a report is loaded
    if (reportUrl && reportConfig && rawReportData) {
      const colorAssignments = getColorAssignmentsObject();
      const hiddenGroups = getHiddenGroupsArray();
      saveRecentReport({
        url: reportUrl,
        reportName: rawReportData.reportName || `Report ${rawReportData.reportId}`,
        reportId: rawReportData.reportId,
        config: { ...reportConfig, colorAssignments, hiddenGroups }
      }).then(updatedRecent => {
        setPinnedReports(updatedRecent.pinned || []);
        setRecentReports(updatedRecent.recent || []);
      }).catch(error => {
        console.error('Failed to auto-save color changes:', error);
      });
    }
  }, [reportUrl, reportConfig, rawReportData]);

  const handleSidebarWidthChange = useCallback((newWidth) => {
    setSidebarWidth(newWidth);
    localStorage.setItem('qbSidebarWidth', newWidth.toString());
  }, []);

  const handleOpenSettings = useCallback(() => {
    setShowSetupDialog(true);
  }, []);

  const handleLoadReportFromTile = useCallback(async (url, reportId, config) => {
    setReportUrl(url);
    setReportConfig(config);
    setLoading(true);
    setError(null);

    // Restore color assignments and hidden groups if saved
    if (config.colorAssignments) {
      restoreColorAssignments(config.colorAssignments);
    }
    if (config.hiddenGroups) {
      restoreHiddenGroups(config.hiddenGroups);
    }

    try {
      const columnMapping = {
        latitude: config.latColumn,
        longitude: config.lngColumn,
      };

      const data = await fetchQuickbaseReport(url, columnMapping);

      setMarkers(data.markers);
      setRawReportData(data);
      setVisibleMarkers(data.markers);

      // Update last accessed time and save color assignments and hidden groups
      try {
        const colorAssignments = getColorAssignmentsObject();
        const hiddenGroups = getHiddenGroupsArray();
        const updatedRecent = await saveRecentReport({
          url,
          reportName: data.reportName || `Report ${data.reportId}`,
          reportId: data.reportId,
          config: { ...config, colorAssignments, hiddenGroups }
        });
        setPinnedReports(updatedRecent.pinned || []);
        setRecentReports(updatedRecent.recent || []);
      } catch (error) {
        console.error('Failed to update recent reports:', error);
      }
    } catch (err) {
      console.error('Failed to load report:', err);
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, []);

  const handlePinReport = useCallback(async (sheet) => {
    try {
      // Use saved config as-is, don't overwrite with current global state
      const updatedData = await pinReport({
        url: sheet.url,
        reportName: sheet.sheetName,
        reportId: sheet.reportId,
        config: sheet.config
      });
      setPinnedReports(updatedData.pinned || []);
      setRecentReports(updatedData.recent || []);
    } catch (error) {
      console.error('Failed to pin report:', error);
      setError(error.message || 'Failed to pin report');
    }
  }, []);

  const handleUnpinReport = useCallback(async (url) => {
    try {
      const updatedData = await unpinReport(url);
      setPinnedReports(updatedData.pinned || []);
      setRecentReports(updatedData.recent || []);
    } catch (error) {
      console.error('Failed to unpin report:', error);
      setError(error.message || 'Failed to unpin report');
    }
  }, []);

  const handleEditReport = useCallback(async (url, tabName, newReportName) => {
    try {
      const updatedData = await updateReportName(url, newReportName);
      setPinnedReports(updatedData.pinned || []);
      setRecentReports(updatedData.recent || []);
    } catch (error) {
      console.error('Failed to update report name:', error);
      setError(error.message || 'Failed to update report name');
    }
  }, []);

  const handleShareReport = useCallback(async (savedMap) => {
    try {
      // If this is the currently loaded map, use current state; otherwise use saved config
      let configToShare = savedMap.config;
      if (savedMap.url === reportUrl && markers.length > 0) {
        const colorAssignments = getColorAssignmentsObject();
        const hiddenGroups = getHiddenGroupsArray();
        configToShare = { ...savedMap.config, colorAssignments, hiddenGroups };
      }

      const shareData = {
        sourceType: 'quickbase',
        sourceUrl: savedMap.url,
        reportId: savedMap.reportId,
        mapName: savedMap.sheetName,
        config: configToShare
      };
      console.log('Creating share with data:', shareData);
      const result = await createSharedMap(shareData);
      return result.shareUrl;
    } catch (error) {
      console.error('Failed to create share link:', error);
      throw error;
    }
  }, [reportUrl, markers]);

  return (
    <div className="SheetMapper">
      <div className="controls">
        <input
          type="text"
          placeholder="Enter QuickBase report URL..."
          value={reportUrl}
          onChange={(e) => setReportUrl(e.target.value)}
          className="sheet-url-input"
          disabled={loading}
        />
        <button
          onClick={handleLoadReport}
          disabled={loading}
          className="load-button"
        >
          {loading ? 'Loading...' : 'Load Report'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {markers.length > 0 && !showSetupDialog && (
        <div className="main-content">
          <Sidebar
            markers={markers}
            selectedMarkers={selectedMarkers}
            onSelectionChange={handleSelectionChange}
            onMarkerClick={(markerId) => {
              console.log('Marker clicked from sidebar:', markerId);
            }}
            onMarkerUpdate={() => {}} // Read-only for now
            onFilterChange={handleFilterChange}
            onColorChange={handleColorChange}
            onGroupVisibilityChange={handleGroupVisibilityChange}
            sheetConfig={reportConfig}
            selectionMode={null}
            onSelectMode={() => {}}
            width={sidebarWidth}
            onWidthChange={handleSidebarWidthChange}
          />

          <div className="map-container">
            <DeckGLMap
              markers={visibleMarkers}
              selectedMarkers={selectedMarkers}
              onMarkerUpdate={() => {}} // Read-only for now
              onSelectionChange={handleSelectionChange}
              sheetConfig={reportConfig}
              selectionMode={null}
              onSelectMode={() => {}}
              colorRefreshKey={colorRefreshKey}
              onOpenSettings={handleOpenSettings}
            />
          </div>
        </div>
      )}

      {markers.length === 0 && !loading && !showSetupDialog && (
        <div className="empty-state-with-sidebar">
          {recentReports.length > 0 && (
            <RecentSheetsSidebar
              recentMaps={recentReports.map(r => ({
                url: r.url,
                sheetName: r.reportName,
                tabName: `Report ${r.reportId}`,
                config: r.config,
                reportId: r.reportId
              }))}
              onLoadMap={(savedMap) => handleLoadReportFromTile(savedMap.url, savedMap.reportId, savedMap.config)}
              onPinMap={handlePinReport}
              iconSrc={qbMapIcon}
            />
          )}
          <div className="empty-state-main">
            {pinnedReports.length > 0 && (
              <RecentSheetsTiles
                pinnedMaps={pinnedReports.map(r => ({
                  url: r.url,
                  sheetName: r.reportName,
                  tabName: `Report ${r.reportId}`,
                  config: r.config,
                  reportId: r.reportId
                }))}
                onLoadMap={(savedMap) => handleLoadReportFromTile(savedMap.url, savedMap.reportId, savedMap.config)}
                onUnpinMap={(url) => handleUnpinReport(url)}
                onEditMap={handleEditReport}
                onShareMap={handleShareReport}
                iconSrc={qbMapIcon}
              />
            )}
            <div className="empty-state-message">
              <p>Enter a QuickBase report URL above to get started</p>
              <p className="help-text">
                Example: https://siteservicesnow.quickbase.com/nav/app/.../table/.../action/q?qid=1189
              </p>
              <p className="help-text">
                Make sure your report has "latitude" and "longitude" columns, or address columns for geocoding
              </p>
            </div>
          </div>
        </div>
      )}

      {showSetupDialog && rawReportData && rawReportData.headers && (
        <SheetSetupDialog
          headers={rawReportData.headers}
          markerCount={rawReportData.markers ? rawReportData.markers.length : 0}
          onComplete={handleSetupComplete}
          onCancel={handleSetupCancel}
          initialConfig={reportConfig}
          disableEditableColumns={true}
        />
      )}
    </div>
  );
}

export default QuickbaseMapper;
